import React, { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import {
  CLUSTERS,
  SYSTEMS,
  buildNetwork,
  buildSegmentGeometry,
  createLabelTexture,
  createRevealLineMaterial,
} from './layout';
import type { SceneState } from './state';
import { smoothstep } from './state';

const INK = '#1a1a1a';
const SLATE = '#3b4553';
const BLUE = '#0066cc';

const PULSE_COUNT = 24;
const PULSES_ON_CONNECTIONS = 14;

interface NetworkProps {
  state: React.MutableRefObject<SceneState>;
  reducedMotion: boolean;
}

interface LabelSpec {
  text: string;
  position: THREE.Vector3;
  kind: 'cluster' | 'system';
  index: number;
}

const Network: React.FC<NetworkProps> = ({ state, reducedMotion }) => {
  const layout = useMemo(() => buildNetwork(), []);
  const nodesRef = useRef<THREE.InstancedMesh>(null);
  const pulsesRef = useRef<THREE.InstancedMesh>(null);
  const labelRefs = useRef<(THREE.Sprite | null)[]>([]);

  const geometries = useMemo(
    () => ({
      cluster: buildSegmentGeometry(layout.nodes, layout.clusterLines),
      connections: buildSegmentGeometry(layout.nodes, layout.connections),
      pathways: buildSegmentGeometry(layout.nodes, layout.pathways),
    }),
    [layout],
  );

  const materials = useMemo(
    () => ({
      cluster: createRevealLineMaterial(INK, 0.18),
      connections: createRevealLineMaterial(BLUE, 0.36),
      pathways: createRevealLineMaterial(BLUE, 0.95),
    }),
    [],
  );

  const labels = useMemo<LabelSpec[]>(
    () => [
      ...CLUSTERS.map((cluster, index) => ({
        text: cluster.label,
        kind: 'cluster' as const,
        index,
        position: new THREE.Vector3(...cluster.center).add(new THREE.Vector3(...cluster.labelOffset)),
      })),
      ...SYSTEMS.map((system, index) => ({
        text: system.label,
        kind: 'system' as const,
        index,
        position: new THREE.Vector3(...system.position).add(new THREE.Vector3(...system.labelOffset)),
      })),
    ],
    [],
  );

  const labelTextures = useMemo(
    () => labels.map((label) => createLabelTexture(label.text, label.kind === 'system' ? INK : '#3a414a')),
    [labels],
  );

  const pulses = useMemo(() => {
    let seed = 7;
    const random = () => {
      seed = (seed * 16807) % 2147483647;
      return seed / 2147483647;
    };
    return Array.from({ length: PULSE_COUNT }, (_, index) => {
      const onPathway = index >= PULSES_ON_CONNECTIONS;
      const pool = onPathway ? layout.pathways : layout.connections;
      const segment = pool[(index * 5) % pool.length];
      return {
        segment,
        onPathway,
        phase: random(),
        speed: onPathway ? 0.22 + random() * 0.1 : 0.1 + random() * 0.09,
      };
    });
  }, [layout]);

  useEffect(() => {
    return () => {
      Object.values(geometries).forEach((geometry) => geometry.dispose());
      Object.values(materials).forEach((material) => material.dispose());
      labelTextures.forEach(({ texture }) => texture.dispose());
    };
  }, [geometries, materials, labelTextures]);

  const scratch = useMemo(
    () => ({
      matrix: new THREE.Matrix4(),
      position: new THREE.Vector3(),
      quaternion: new THREE.Quaternion(),
      scale: new THREE.Vector3(),
      color: new THREE.Color(),
      ink: new THREE.Color(INK),
      slate: new THREE.Color(SLATE),
      blue: new THREE.Color(BLUE),
    }),
    [],
  );

  useFrame(({ clock }) => {
    const { signals, connections, focus, outro } = state.current;
    const hold = 1 - smoothstep(outro, 0, 0.55);
    const time = reducedMotion ? 0 : clock.elapsedTime;
    const nodes = nodesRef.current;
    const pulsesMesh = pulsesRef.current;
    if (!nodes || !pulsesMesh) return;

    const settle = smoothstep(focus, 0.2, 1);

    layout.nodes.forEach((node, index) => {
      let reveal: number;
      let radius: number;
      if (node.system >= 0) {
        reveal = smoothstep(connections, 0.15 + node.order * 0.5, 0.45 + node.order * 0.5);
        radius = 0.04 * (1 + settle * 0.4);
        scratch.color.copy(scratch.ink).lerp(scratch.blue, settle * 0.85);
      } else {
        reveal = smoothstep(signals, node.order * 0.7, node.order * 0.7 + 0.3);
        if (node.hub) {
          const breathe = 1 + Math.sin(time * 2.1 + index) * 0.08 * settle;
          radius = 0.024 * (1 + settle * 1.4) * breathe;
          scratch.color.copy(scratch.slate).lerp(scratch.blue, settle);
        } else {
          radius = 0.02 * (1 - settle * 0.3);
          scratch.color.copy(scratch.slate).lerp(scratch.ink, settle * 0.4);
        }
      }

      const size = radius * reveal * hold;
      scratch.scale.setScalar(Math.max(size, 0.0001));
      scratch.matrix.compose(node.position, scratch.quaternion, scratch.scale);
      nodes.setMatrixAt(index, scratch.matrix);
      nodes.setColorAt(index, scratch.color);
    });
    nodes.instanceMatrix.needsUpdate = true;
    if (nodes.instanceColor) nodes.instanceColor.needsUpdate = true;

    materials.cluster.uniforms.uReveal.value = smoothstep(signals, 0.35, 1);
    materials.cluster.uniforms.uOpacity.value = 0.18 * (1 - settle * 0.55) * hold;
    materials.connections.uniforms.uReveal.value = smoothstep(connections, 0.08, 1);
    materials.connections.uniforms.uOpacity.value = 0.36 * (1 - settle * 0.62) * hold;
    materials.pathways.uniforms.uReveal.value = smoothstep(focus, 0.12, 0.95);
    materials.pathways.uniforms.uOpacity.value = 0.95 * hold;

    labels.forEach((label, index) => {
      const sprite = labelRefs.current[index];
      if (!sprite) return;
      const material = sprite.material as THREE.SpriteMaterial;
      if (label.kind === 'cluster') {
        material.opacity =
          smoothstep(signals, 0.62 + label.index * 0.06, 0.92 + label.index * 0.06) * hold;
      } else {
        material.opacity =
          smoothstep(connections, 0.45 + label.index * 0.07, 0.8 + label.index * 0.07) *
          (1 - settle * 0.35) *
          hold;
      }
    });

    const connectionStrength = reducedMotion ? 0 : smoothstep(connections, 0.55, 1) * (1 - settle) * hold;
    const pathwayStrength = reducedMotion ? 0 : smoothstep(focus, 0.45, 1) * hold;
    pulses.forEach((pulse, index) => {
      const strength = pulse.onPathway ? pathwayStrength : connectionStrength;
      const progress = (time * pulse.speed + pulse.phase) % 1;
      const a = layout.nodes[pulse.segment.a].position;
      const b = layout.nodes[pulse.segment.b].position;
      scratch.position.lerpVectors(a, b, progress);
      const taper = Math.sin(progress * Math.PI);
      const size = (pulse.onPathway ? 0.03 : 0.022) * strength * (0.4 + taper * 0.6);
      scratch.scale.setScalar(Math.max(size, 0.0001));
      scratch.matrix.compose(scratch.position, scratch.quaternion, scratch.scale);
      pulsesMesh.setMatrixAt(index, scratch.matrix);
    });
    pulsesMesh.instanceMatrix.needsUpdate = true;
  });

  return (
    <group>
      <instancedMesh ref={nodesRef} args={[undefined, undefined, layout.nodes.length]} frustumCulled={false}>
        <sphereGeometry args={[1, 14, 14]} />
        <meshBasicMaterial toneMapped={false} />
      </instancedMesh>

      <lineSegments geometry={geometries.cluster} material={materials.cluster} frustumCulled={false} />
      <lineSegments geometry={geometries.connections} material={materials.connections} frustumCulled={false} />
      <lineSegments geometry={geometries.pathways} material={materials.pathways} frustumCulled={false} />

      <instancedMesh ref={pulsesRef} args={[undefined, undefined, PULSE_COUNT]} frustumCulled={false}>
        <sphereGeometry args={[1, 10, 10]} />
        <meshBasicMaterial color={BLUE} toneMapped={false} />
      </instancedMesh>

      {labels.map((label, index) => (
        <sprite
          key={label.text}
          ref={(sprite) => {
            labelRefs.current[index] = sprite;
          }}
          position={label.position}
          scale={[
            label.kind === 'system' ? 0.62 : 0.74,
            (label.kind === 'system' ? 0.62 : 0.74) / labelTextures[index].aspect,
            1,
          ]}
        >
          <spriteMaterial
            map={labelTextures[index].texture}
            transparent
            opacity={0}
            depthWrite={false}
            toneMapped={false}
          />
        </sprite>
      ))}
    </group>
  );
};

export default Network;
